import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface AIModelDetail {
  id: number;
  modelId: string;
  name: string;
  provider: string;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  capabilities: string[];
}

export interface AdminAIModelDetailPageProps {
  model: AIModelDetail;

  // URLs
  dashboardPath: string;
  modelsPath: string;

  // Labels
  labels: {
    title: string;
    dashboardLabel: string;
    modelsLabel: string;
    allModelsButton: string;
    // Details
    modelDetailsTitle: string;
    modelIdLabel: string;
    providerLabel: string;
    contextWindowLabel: string;
    maxOutputLabel: string;
    tokensLabel: string;
    capabilitiesLabel: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function AINavigation({
  activeTab,
  dashboardPath,
  modelsPath,
  labels,
}: {
  activeTab: 'dashboard' | 'models';
  dashboardPath: string;
  modelsPath: string;
  labels: AdminAIModelDetailPageProps['labels'];
}) {
  return (
    <nav className="mb-4">
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <a
            href={dashboardPath}
            className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
          >
            <i className="bi bi-speedometer2 me-1" />
            {labels.dashboardLabel}
          </a>
        </li>
        <li className="nav-item">
          <a href={modelsPath} className={`nav-link ${activeTab === 'models' ? 'active' : ''}`}>
            <i className="bi bi-cpu me-1" />
            {labels.modelsLabel}
          </a>
        </li>
      </ul>
    </nav>
  );
}

function formatNumber(num: number | null): string {
  if (num === null || num === undefined) return '-';
  return num.toLocaleString();
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminAIModelDetailPage({
  model,
  dashboardPath,
  modelsPath,
  labels,
}: AdminAIModelDetailPageProps) {
  return (
    <div>
      <AINavigation
        activeTab="models"
        dashboardPath={dashboardPath}
        modelsPath={modelsPath}
        labels={labels}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h2 mb-2">
            <i className="bi bi-cpu me-2" />
            {model.name}
          </h1>
          <span className="badge bg-primary">{model.provider}</span>
        </div>
        <div>
          <a href={modelsPath} className="btn btn-outline-secondary">
            <i className="bi bi-arrow-left me-1" />
            {labels.allModelsButton}
          </a>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-info-circle me-2" />
                {labels.modelDetailsTitle}
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 bg-light rounded">
                    <div className="text-muted small mb-1">{labels.modelIdLabel}</div>
                    <div className="fw-bold">{model.modelId}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 bg-light rounded">
                    <div className="text-muted small mb-1">{labels.providerLabel}</div>
                    <div className="fw-bold">{model.provider}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 bg-light rounded">
                    <div className="text-muted small mb-1">{labels.contextWindowLabel}</div>
                    <div className="fw-bold">
                      {formatNumber(model.contextWindow)} {labels.tokensLabel}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 bg-light rounded">
                    <div className="text-muted small mb-1">{labels.maxOutputLabel}</div>
                    <div className="fw-bold">
                      {formatNumber(model.maxOutputTokens)} {labels.tokensLabel}
                    </div>
                  </div>
                </div>
              </div>

              {model.capabilities.length > 0 && (
                <div className="mt-4">
                  <div className="text-muted small mb-2">{labels.capabilitiesLabel}</div>
                  <div>
                    {model.capabilities.map((capability, index) => (
                      <span key={index} className="badge bg-secondary me-1 mb-1">
                        {capability}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAIModelDetailPage;
